import { useMemo } from "react";
import { Link } from "react-router-dom";
import { format, getISOWeek, getYear, parseISO, isBefore, startOfWeek } from "date-fns";
import { nb } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { usePMProjects } from "@/hooks/use-pm-projects";
import { useUpcomingTasks } from "@/hooks/use-upcoming-tasks";
import type { WeekTask } from "@/hooks/use-week-tasks";
import { SrIcon } from "@/components/ui/SrIcon";

const STATUS_LABELS: Record<string, string> = {
  not_started: "Ikke påbegynt",
  in_progress: "Pågår",
  completed: "Venter godkjenning",
  rejected: "Avvist",
};

const STATUS_BADGE: Record<string, string> = {
  not_started: "bg-card text-foreground border-foreground",
  in_progress: "bg-[var(--sr-reveblaa)] text-white border-[var(--sr-reveblaa)]",
  completed: "bg-[#FEF3C7] text-[#78350F] border-foreground",
  rejected: "bg-[#FFE1DB] text-[#7F1D1D] border-foreground",
};

interface GroupedWeek {
  key: string; // "2026-W17"
  weekNumber: number;
  year: number;
  label: string;
  tasks: WeekTask[];
  isCurrentWeek: boolean;
}

/**
 * VIB-574: Ukevisning som erstatter kalendervisningen i Mine oppgaver.
 * Grupperer oppgaver etter ISO-uke basert på deres estimated_deadline.
 */
export default function WeeklyTasksList() {
  const { activeUser } = useImpersonation();
  const userId = activeUser?.id ?? null;
  const { data: pmProjects } = usePMProjects(userId);
  const pmProjectIds = useMemo(
    () => (pmProjects ?? []).map((p) => p.id),
    [pmProjects],
  );

  const { data: tasks, isLoading } = useUpcomingTasks(
    userId,
    pmProjectIds.length > 0 ? pmProjectIds : undefined,
  );

  const groups = useMemo<GroupedWeek[]>(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const currentWeekKey = `${getYear(today)}-W${getISOWeek(today)}`;
    const map = new Map<string, GroupedWeek>();
    for (const task of tasks ?? []) {
      if (!task.estimated_deadline) continue;
      const date = parseISO(task.estimated_deadline);
      const week = getISOWeek(date);
      const year = getYear(date);
      const key = `${year}-W${week}`;
      let group = map.get(key);
      if (!group) {
        const monday = startOfWeek(date, { weekStartsOn: 1 });
        group = {
          key,
          weekNumber: week,
          year,
          label: `Uke ${week} · ${format(monday, "d. MMM", { locale: nb })}`,
          tasks: [],
          isCurrentWeek: key === currentWeekKey,
        };
        map.set(key, group);
      }
      group.tasks.push(task);
    }
    return [...map.values()].sort((a, b) => a.key.localeCompare(b.key));
  }, [tasks]);

  if (isLoading) {
    return (
      <div className="border-[1.5px] border-foreground bg-card p-12 text-center text-muted-foreground">
        Laster oppgaver…
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="border-[1.5px] border-foreground bg-card p-12 text-center">
        <p className="text-sm text-muted-foreground">
          Ingen oppgaver med deadline fremover. Sett en deadline på oppgavene dine i
          Mine oppgaver.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {groups.map((group) => (
        <WeekGroup key={group.key} group={group} />
      ))}
    </div>
  );
}

function WeekGroup({ group }: { group: GroupedWeek }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <div className="border-[1.5px] border-foreground bg-card">
      <div
        className={cn(
          "px-4 py-3 border-b-[1.5px] border-foreground flex items-center gap-3",
          group.isCurrentWeek && "bg-primary/10",
        )}
      >
        <SrIcon name="calendar" className="w-4 h-4 text-muted-foreground" />
        <span className="font-display font-semibold text-base tracking-tight">{group.label}</span>
        {group.isCurrentWeek && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full border-[1.5px] border-primary bg-primary text-white text-[11px] font-medium">
            Denne uken
          </span>
        )}
        <span className="ml-auto text-[12px] text-muted-foreground tabular-nums">
          {group.tasks.length} oppgave{group.tasks.length === 1 ? "" : "r"}
        </span>
      </div>
      <ul className="divide-y divide-border/50">
        {group.tasks.map((task) => {
          const deadline = parseISO(task.estimated_deadline);
          const isOverdue = isBefore(deadline, today) && task.task_status !== "completed";
          return (
            <li key={task.id} className="px-4 py-2.5 flex items-center gap-3 hover:bg-muted/40">
              <div className="flex flex-col items-center w-12 shrink-0">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {format(deadline, "EEE", { locale: nb })}
                </span>
                <span
                  className={cn(
                    "text-[15px] font-display font-semibold tabular-nums",
                    isOverdue && "text-destructive",
                  )}
                >
                  {format(deadline, "d.")}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <Link
                  to={`/prosjekter/${task.project_id}`}
                  className="text-sm font-medium hover:text-primary line-clamp-1"
                >
                  {task.description}
                </Link>
                <p className="text-[12px] text-muted-foreground line-clamp-1">
                  {task.project_name}
                  {task.project_code && (
                    <span className="ml-1 tabular-nums opacity-70">{task.project_code}</span>
                  )}
                  <span className="mx-1.5">·</span>
                  <span className="capitalize">{task.phase}</span>
                  <span className="mx-1.5">·</span>
                  <span>{task.user_name.split(" ")[0]}</span>
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-[12px] tabular-nums text-muted-foreground">{task.hours}t</span>
                <span
                  className={cn(
                    "inline-flex items-center text-[10.5px] px-2 py-0.5 rounded-full border-[1.5px] font-medium",
                    isOverdue
                      ? "bg-[#FFE1DB] text-[#7F1D1D] border-foreground"
                      : STATUS_BADGE[task.task_status] ?? STATUS_BADGE.not_started,
                  )}
                >
                  {isOverdue ? "Forsinket" : STATUS_LABELS[task.task_status] ?? task.task_status}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
